/**
 * Encryption Service
 * Provides encryption and decryption for sensitive data
 */

export class EncryptionService {
  private algorithm = 'AES-GCM';
  private keyLength = 256;
  private saltLength = 16;
  private ivLength = 12;
  private tagLength = 128;
  private iterations = 100000;

  /**
   * Derives a key from a password using PBKDF2
   */
  private async deriveKey(
    password: string,
    salt: Uint8Array
  ): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const passwordBuffer = encoder.encode(password);

    const importedKey = await crypto.subtle.importKey(
      'raw',
      passwordBuffer,
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: this.iterations,
        hash: 'SHA-256',
      },
      importedKey,
      {
        name: this.algorithm,
        length: this.keyLength,
      },
      false,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Gets or generates the encryption password
   */
  private async getPassword(): Promise<string> {
    if (typeof window === 'undefined') {
      // Server-side: use environment variable
      return process.env.ENCRYPTION_KEY || 'default-dev-key-change-in-production';
    }

    // Client-side: use or generate a unique password
    const storageKey = 'vibecode-encryption-id';
    let password = localStorage.getItem(storageKey);
    
    if (!password) {
      // Generate a unique password for this browser
      password = this.generatePassword();
      localStorage.setItem(storageKey, password);
    }
    
    return password;
  }

  /**
   * Generates a secure random password
   */
  private generatePassword(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Encrypts a string
   */
  async encrypt(plaintext: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(plaintext);

    // Generate random salt and IV
    const salt = crypto.getRandomValues(new Uint8Array(this.saltLength));
    const iv = crypto.getRandomValues(new Uint8Array(this.ivLength));

    // Derive key from password
    const password = await this.getPassword();
    const key = await this.deriveKey(password, salt);

    // Encrypt the data
    const encrypted = await crypto.subtle.encrypt(
      {
        name: this.algorithm,
        iv,
        tagLength: this.tagLength,
      },
      key,
      data
    );

    // Combine salt, iv, and encrypted data
    const combined = new Uint8Array(
      salt.length + iv.length + encrypted.byteLength
    );
    combined.set(salt, 0);
    combined.set(iv, salt.length);
    combined.set(new Uint8Array(encrypted), salt.length + iv.length);

    // Convert to base64 for storage
    return btoa(String.fromCharCode.apply(null, Array.from(combined)));
  }

  /**
   * Decrypts a string
   */
  async decrypt(ciphertext: string): Promise<string> {
    // Convert from base64
    const combined = Uint8Array.from(atob(ciphertext), c => c.charCodeAt(0));

    // Extract salt, iv, and encrypted data
    const salt = combined.slice(0, this.saltLength);
    const iv = combined.slice(this.saltLength, this.saltLength + this.ivLength);
    const encrypted = combined.slice(this.saltLength + this.ivLength);

    // Derive key from password
    const password = await this.getPassword();
    const key = await this.deriveKey(password, salt);

    // Decrypt the data
    const decrypted = await crypto.subtle.decrypt(
      {
        name: this.algorithm,
        iv,
        tagLength: this.tagLength,
      },
      key,
      encrypted
    );

    // Convert back to string
    const decoder = new TextDecoder();
    return decoder.decode(decrypted);
  }

  /**
   * Checks if Web Crypto API is available
   */
  isAvailable(): boolean {
    return typeof crypto !== 'undefined' && crypto.subtle !== undefined;
  }
}